import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import TableAlt from "../components/elements/table/TableAlt";
import ListLayout from "../containers/ListLayout";
import { postData } from "../services/api";
import toastr from "toastr";
import AddEditUsersModal from "../components/modals/AddEditUserModal";
import { useDispatch } from "react-redux";
import { courseListData, listData, userListData, userTabChange } from "../store/companiesSlice";
import { API_URL } from '../config/host';
import { setTitle } from "../helpers/MetaTag";


const Users = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const tabChangeStatus = useSelector((state) => state.companyListData.tabChange);
  const [statusFilterData, setStatusFilterData] = useState([
    { _id: 1, name: 'Active', type: 'checkbox', value: 1, isChecked: false },
    { _id: 2, name: 'Inactive', type: 'checkbox', value: 2, isChecked: false },
  ]);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [dateRange, setDateRange] = useState({})
  const [list, setList] = useState({
    loading: true,
    data: [],
    count: 0,
  });
  const [sortField, setSortField] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState({});

  setTitle("Radisol | Users");

  useEffect(() => {
    if (user?._id) {
      dispatch(userTabChange())

      const loadData = async () => {
        setList((list) => ({ ...list, loading: true }));
        const searchParams = {};
        if (keyword) {
          searchParams.name = { $regex: keyword, $options: "i" };
        }
        const checkedStatusFilter = statusFilterData.filter((id) => id.isChecked)
        if (checkedStatusFilter.length > 0) {
          searchParams.status = checkedStatusFilter.map((item) => item.value)
        }

        const res = await postData("user/list", {
          ...searchParams,
          type: "user",
          keyword: keyword,
          limit,
          offset,
          sortQuery:
            sortField && sortType ? { [sortField]: sortType } : { ordering: 1 },
        });
        if (res.status === 200) {
          setList((list) => ({
            ...list,
            data: res.data,
            loading: false,
            count: res.count,
          }));
        } else {
          setList((list) => ({ ...list, loading: false }));
        }
      };

      loadData();
    }
  }, [
    user?._id,
    keyword,
    limit,
    offset,
    sortField,
    sortType,
    statusFilterData,
  ]);

  // company list add in redux store
  useEffect(() => {
    const loadData = async () => {
      const res = await postData("user/list", {
        type: "company",
        status: true,
      });
      if (res.status === 200) {
        dispatch(listData(res.data))
      }
    };

    loadData();
  }, [tabChangeStatus, dispatch])

  // user list add in redux store
  useEffect(() => {
    const loadData = async () => {
      const res = await postData("user/list", {
        type: "user",
        status: true
      });
      if (res.status === 200) {
        dispatch(userListData(res.data))
      }
    };

    loadData();
  }, [tabChangeStatus, dispatch])

  // course list
  useEffect(() => {
    if (user?._id) {
      const loadData = async () => {
        const res = await postData('crud/list', {
          table_name: 'course',
          dateRange,
          queryParams: {
            deleted_at: null,
            status: true,
          },
          selectParams: {
            title: true,
            sub_title: true,
            description: true,
            image: {
              $cond: [
                '$image', { $concat: [API_URL, '$image'] }, null
              ]
            },
            ordering: true,
            created_at: true,
            category_details: {
              $cond: [
                { $arrayElemAt: ["$category_details", 0] }, { $arrayElemAt: ["$category_details", 0] }, {}
              ]
            },
          },
          join: [
            {
              toTable: "categories",
              fromField: "category_id",
              toField: "_id",
              selectField: "category_details",
              projection: {
                name: true, status: true
              }
            },
          ],
          sortQuery: sortField && sortType ? { [sortField]: sortType } : { ordering: 1 },
        });
        if (res.status === 200) {
          dispatch(courseListData(res.data.list))
        }
      };

      loadData();
    }
  }, [user?._id, tabChangeStatus, dispatch]);

  const changeStatus = async (id) => {
    const res = await postData('user/status/manage', {
      id: id
    });

    if (res.status === 200) {
      setList((list) => ({
        ...list,
        data: list.data.map((item) =>
          item._id !== id
            ? item
            : {
              ...item,
              status: !(
                list.data.find((item) => item._id === id).status || false
              ),
            }
        ),
      }));
      toastr.success(res.message);
    }
  };

  const actionData = (item) => [
    {
      name: "Edit",
      icon: "fa-pen-to-square",
      type: "button",
      onClick: (id) => {
        setShowModal(true);
        setEditData(list.data.find((item) => item._id === id) || {});
      },
    },
    {
      name: item.status ? 'Inactive' : 'Active',
      icon: item.status ? 'fa-circle-exclamation' : 'fa-circle-check',
      type: 'button',
      onClick: changeStatus,
    },
    {
      name: "Preview",
      icon: "fa-eye",
      type: "link",
      link: "/user-details",
    },
    {
      name: "Delete",
      icon: "fa-trash-can",
      type: "button",
      actionType: "delete",
      deleteLink: "user/delete",
      deletePayload: { id: item._id },
      standout: true
    },
  ];

  const tableHeadData = [
    {
      _id: 1,
      width: 15,
      title: "Name",
      name: "full_name",
      align: "left",
      sortable: true,
      filterable: false,
    },
    {
      _id: 2,
      width: 15,
      title: "Company",
      name: "company_name",
      align: "left",
      sortable: true,
      filterable: false,
    },
    {
      _id: 3,
      width: 15,
      title: "Email",
      name: "email",
      align: "left",
      sortable: true,
      filterable: false,
    },
    {
      _id: 4,
      width: 15,
      title: "Phone",
      name: "phone",
      align: "left",
      sortable: true,
      filterable: false,
    },
    {
      _id: 5,
      width: 15,
      title: "Created At",
      name: "created_at",
      align: "left",
      sortable: true,
      filterable: false,
    },
    {
      _id: 6,
      width: 15,
      title: 'Status',
      name: 'status',
      align: 'left',
      sortable: true,
      filterable: true,
      filterOptions: statusFilterData,
      onFilterChange: (id) => {
        setStatusFilterData((data) =>
          data.map((item) =>
            item._id !== id
              ? item
              : {
                ...item,
                isChecked: !item.isChecked,
              }
          )
        );
      },
    },
    {
      _id: 7,
      title: "Action",
      action: true,
    },
  ];

  return (
    <>
      <ListLayout
        title="Users"
        addName="Add"
        onAdd={() => setShowModal(true)}
        limit={limit}
        setLimit={setLimit}
        setKeyword={setKeyword}
        keyword={keyword}
        noOfPage={Math.ceil(list.count / limit)}
        onPageChange={(val) => setOffset(val.selected * limit)}
        setOffset={setOffset}
      >
        <TableAlt
          tableHeadData={tableHeadData}
          tableData={list.data}
          loading={list.loading}
          sortField={sortField}
          sortType={sortType}
          setSortField={setSortField}
          setSortType={setSortType}
          actionData={actionData}
          setList={setList}
          isDraggable={false}
          functions={changeStatus}
        />
      </ListLayout>
      <AddEditUsersModal
        show={showModal}
        setShow={setShowModal}
        title={Object.keys(editData).length > 0 ? "Edit User" : "Add User"}
        list={list.data}
        setList={setList}
        editData={editData}
        setEditData={setEditData}
      />
    </>
  );
};

export default Users;
